import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Smile, Frown, Heart, Flame, Ghost, Sparkles, Cloud, Sun,
  Zap, Coffee, Star, Search, X, Play, Plus, Check, Link as LinkIcon, Trash2
} from "lucide-react";

/** ---------- Data ---------- */
const MOODS = [
  { id: "happy", label: "Happy", desc: "Feel-good & uplifting", Icon: Smile, gradient: "linear-gradient(135deg,#fbbf24,#f97316)" },
  { id: "sad", label: "Sad", desc: "Cathartic drama", Icon: Frown, gradient: "linear-gradient(135deg,#38bdf8,#4f46e5)" },
  { id: "romantic", label: "Romantic", desc: "Love & rom-coms", Icon: Heart, gradient: "linear-gradient(135deg,#f43f5e,#ec4899)" },
  { id: "excited", label: "Excited", desc: "Action & pace", Icon: Flame, gradient: "linear-gradient(135deg,#facc15,#ef4444)" },
  { id: "scared", label: "Scared", desc: "Thriller & horror", Icon: Ghost, gradient: "linear-gradient(135deg,#3f3f46,#0a0a0a)" },
  { id: "inspired", label: "Inspired", desc: "Biopic & triumph", Icon: Sparkles, gradient: "linear-gradient(135deg,#34d399,#0d9488)" },
  { id: "calm", label: "Calm", desc: "Slow, serene, zen", Icon: Cloud, gradient: "linear-gradient(135deg,#22d3ee,#3b82f6)" },
  { id: "tense", label: "Tense", desc: "Mystery & suspense", Icon: Zap, gradient: "linear-gradient(135deg,#a855f7,#c026d3)" },
  { id: "nostalgic", label: "Nostalgic", desc: "Retro & classics", Icon: Coffee, gradient: "linear-gradient(135deg,#b45309,#7c2d12)" },
  { id: "hopeful", label: "Hopeful", desc: "Optimistic & uplifting", Icon: Star, gradient: "linear-gradient(135deg,#a3e635,#16a34a)" },
];

const moodToGradient = Object.fromEntries(MOODS.map(m => [m.id, m.gradient]));
const ytThumb   = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
const ytThumbHQ = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const CATALOG = [
  { id: "m1", title: "Inside Out", year: 2015, rating: 8.1, moods: ["happy", "inspired"], length: 95, trailerId: "seMwpP0yeu4", description: "A joyous, inventive adventure inside the mind of an 11-year-old girl." },
  { id: "m2", title: "La La Land", year: 2016, rating: 8.0, moods: ["romantic", "happy"], length: 128, trailerId: "0pdqf4P9MB8", description: "A musician and an actress chase love and dreams in Los Angeles." },
  { id: "m3", title: "Inception", year: 2010, rating: 8.8, moods: ["excited", "tense"], length: 148, trailerId: "YoHD9XEInc0", description: "A thief who enters dreams must pull off the ultimate inception." },
  { id: "m4", title: "The Notebook", year: 2004, rating: 7.9, moods: ["romantic", "sad"], length: 123, trailerId: "yDJIcYE32NU", description: "A sweeping love story spanning decades and second chances." },
  { id: "m5", title: "Get Out", year: 2017, rating: 7.7, moods: ["scared", "tense"], length: 104, trailerId: "DzfpyUB60YY", description: "A visit to the girlfriend's family uncovers chilling secrets." },
  { id: "m6", title: "Interstellar", year: 2014, rating: 8.6, moods: ["inspired", "hopeful"], length: 169, trailerId: "zSWdZVtXT7E", description: "Explorers travel through a wormhole to save humanity." },
  { id: "m7", title: "Parasite", year: 2019, rating: 8.6, moods: ["tense", "sad"], length: 132, trailerId: "5xH0HfJHsaY", description: "Two families become entangled in a darkly comic thriller." },
  { id: "m8", title: "A Quiet Place", year: 2018, rating: 7.5, moods: ["scared", "tense"], length: 90, trailerId: "WR7cc5t7tv8", description: "A family must live in silence to hide from sound-hunting creatures." },
  { id: "m16", title: "Spider-Man", year: 2023, rating: 8.9, moods: ["excited", "inspired"], length: 140, trailerId: "cqGjhVJWtEg", description: "Miles Morales embarks on a multiverse-spanning adventure with Gwen Stacy." },
  { id: "m10", title: "Whiplash", year: 2014, rating: 8.5, moods: ["inspired", "tense"], length: 107, trailerId: "7d_jQycdQGo", description: "An ambitious drummer faces an abusive instructor." },
  { id: "m11", title: "The Social Network", year: 2010, rating: 7.8, moods: ["tense"], length: 120, trailerId: "lB95KLmpLR4", description: "The founding of Facebook sparks ambition and betrayal." },
  { id: "m12", title: "Spirited Away", year: 2001, rating: 8.6, moods: ["calm", "happy"], length: 125, trailerId: "ByXuk9QqQkk", description: "A young girl enters a world of spirits and self-discovery." },
  { id: "m22", title: "Joker", year: 2025, rating: 9.0, moods: ["tense", "scared"], length: 160, trailerId: "EXeTwQWrcwY", description: "The next chapter in Matt Reeves’ gritty Batman saga." },
  { id: "m14", title: "Arrival", year: 2016, rating: 7.9, moods: ["calm", "inspired", "tense"], length: 116, trailerId: "tFMo3UJ4B4g", description: "A linguist communicates with aliens to decode time and meaning." },
  { id: "m26", title: "Mission: Impossible", year: 2023, rating: 7.8, moods: ["excited", "tense"], length: 163, trailerId: "2m1drlOZSDw", description: "Ethan Hunt races against time to stop a deadly new weapon." },
];

/** ---------- Breakpoints & Responsive helpers ---------- */
function useDevice() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR, { passive: true });
    return () => window.removeEventListener("resize", onR);
  }, []);
  return {
    w,
    isXs: w < 480,
    isSm: w < 640,
    isMd: w >= 640 && w < 900,
    isLg: w >= 900 && w < 1200,
    isXl: w >= 1200,
  };
}

function useCols(w){
  if (w >= 1200) return 5;
  if (w >= 900)  return 4;
  if (w >= 640)  return 3;
  if (w >= 480)  return 2; // small phones landscape / small tablets
  return 1;                 // phones portrait → single column
}

/** ---------- Styles ---------- */
const fallbackImg = "https://via.placeholder.com/400x600?text=No+Image";
const S = {
  shell: {
    minHeight: "100vh",
    background: "linear-gradient(to bottom,#0b0b0c,#101013)",
    color: "#fff",
    fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,Inter,Arial,sans-serif',
  },
  container: { maxWidth: 1200, margin: "0 auto", padding: "24px 16px" },

  header: {
    position: "sticky", top: 0, zIndex: 40,
    background: "rgba(16,16,19,.7)",
    backdropFilter: "saturate(120%) blur(8px)",
    borderBottom: "1px solid rgba(255,255,255,.12)"
  },
  headerInner: { maxWidth: 1200, margin: "0 auto", padding: "10px 12px", display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" },
  brand: { display: "flex", gap: 10, alignItems: "center", flex: "0 0 auto" },
  logo: {
    width: 36, height: 36, borderRadius: 10, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg,#7c5cff,#ff55bb)",
    boxShadow: "0 6px 24px rgba(0,0,0,.4)",
    flex: "0 0 auto"
  },
  title: { fontWeight: 900, fontSize: 18, letterSpacing: .2 },
  sub:   { color: "#b5b5b7", fontSize: 12 },

  headRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: "1",
    justifyContent: "flex-end",
    minWidth: 0
  },

  searchWrap: {
    position: "relative",
    flex: "0 1 420px",
    maxWidth: 420,
    width: "100%"
  },
  searchInput: {
    width: "100%", padding: "10px 38px 10px 38px",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 12, background: "rgba(255,255,255,.05)",
    color: "#fff", outline: "none", fontSize: 14
  },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", opacity: .6, pointerEvents: "none" },
  clearBtn: { position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", padding: 6, borderRadius: 8, cursor: "pointer" },

  favBtn: {
    position: "relative",
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.06)",
    color: "#fff",
    borderRadius: 12,
    padding: "8px 10px",
    display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
    flex: "0 0 auto"
  },
  favBadge: {
    position: "absolute", top: -6, right: -6,
    background: "#ff4d6d", color: "#fff",
    fontSize: 11, borderRadius: 999, padding: "2px 6px",
    border: "2px solid #0b0b0c"
  },

  nav: {
    display: "flex", flexWrap: "nowrap", gap: 8, overflowX: "auto",
    maxWidth: 1200, margin: "0 auto", padding: "8px 12px 12px"
  },
  chip: (active) => ({
    border: "1px solid rgba(255,255,255,.12)",
    background: active ? "#fff" : "rgba(255,255,255,.05)",
    color: active ? "#000" : "#fff",
    borderRadius: 999, padding: "8px 14px", fontSize: 14,
    display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
    whiteSpace: "nowrap"
  }),

  card: {
    background: "rgba(255,255,255,.05)",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 16, overflow: "hidden",
    boxShadow: "0 6px 24px rgba(0,0,0,.4)",
  },
  cardBody: { padding: 14 },

  poster: (big) => ({ position: "relative", width: "100%", height: big ? 224 : 176, background: "#1a1a1a" }),
  overlayTop: { position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.5), transparent)", pointerEvents: "none" },

  metaRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" },
  title3: { fontSize: 16, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 300 },
  muted: { color: "#b5b5b7", fontSize: 12 },

  rating: { display: "flex", alignItems: "center", gap: 6, fontSize: 12 },

  badge: { background: "rgba(255,255,255,.12)", padding: "2px 8px", borderRadius: 999, fontSize: 12 },
  badgesRow: { display: "flex", gap: 8, flexWrap: "wrap" },

  actions: { display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" },
  btn: {
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.05)",
    color: "#fff",
    padding: "8px 12px", borderRadius: 10, fontSize: 13,
    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer"
  },
  btnPrimary: { background: "#fff", color: "#000", border: "1px solid rgba(255,255,255,.2)" },

  // Results grid
  grid: (cols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 12,
    justifyContent: "center",
    maxWidth: 1200,
    margin: "0 auto",
  }),

  // Favorites panel
  favPanelWrap: { position: "relative", flex: "0 0 auto" },
  favPanel: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    width: 360,
    maxHeight: 420,
    overflow: "auto",
    background: "#0b0b0c",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,.5)",
    padding: 12,
    zIndex: 50
  },
  favItem: {
    display: "grid",
    gridTemplateColumns: "64px 1fr auto",
    gap: 10,
    alignItems: "center",
    padding: "8px 6px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.06)",
    background: "rgba(255,255,255,.03)"
  },
  favThumb: { width: 64, height: 40, borderRadius: 8, overflow: "hidden", background: "#222" },

  // Modal centered
  modalOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,.6)",
    display: "flex", justifyContent: "center", alignItems: "center",
    padding: 0, zIndex: 60
  },
  modal: {
    width: "100%", maxWidth: 720, background: "#0b0b0c", color: "#fff",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 20, overflow: "hidden",
    boxShadow: "0 6px 24px rgba(0,0,0,.4)"
  },
  modalTop: { position: "relative", height: 224 },
  modalTopOverlay: { position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.6), transparent)" },
  modalBody: { padding: 18 },
  modalHead: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" },

  footer: { maxWidth: 1200, margin: "0 auto", padding: "32px 16px", color: "#b5b5b7", fontSize: 12 },
};

/** ---------- Helpers ---------- */
function Rating({ value }) {
  return (
    <div style={S.rating}>
      <Star size={16} /><span>{Number(value).toFixed(1)}</span>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button style={S.chip(active)} onClick={onClick}>{children}</button>
  );
}

function Card({ children }) {
  return <div style={S.card}>{children}</div>;
}

function Poster({ id, alt, big }) {
  const [src, setSrc] = useState(ytThumb(id));
  return (
    <div style={S.poster(big)}>
      <img
        src={src || fallbackImg}
        onError={() => setSrc(fallbackImg)}
        alt={alt}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
      <div style={S.overlayTop} />
    </div>
  );
}

/** ---------- Favorites Panel ---------- */
function FavoritesButton({ items, onOpenToggle }) {
  return (
    <div style={S.favPanelWrap}>
      <button style={S.favBtn} onClick={onOpenToggle} aria-label="Favorites">
        <Heart size={18} />
        <span className="hide-sm" style={{fontSize:13}}>Favorites</span>
        {items.length > 0 && <span style={S.favBadge}>{items.length}</span>}
      </button>
    </div>
  );
}

function FavoritesPanel({ open, watchlist, onOpenMovie, onRemove, isSm }) {
  if (!open) return null;
  const panelStyle = isSm
    ? { ...S.favPanel, position: "fixed", top: "auto", bottom: 12, left: 12, right: 12, width: "auto", maxHeight: "55vh", marginTop: 0 }
    : S.favPanel;
  return (
    <div style={panelStyle} role="dialog" aria-label="Favorites list">
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
        <strong>Favorites</strong>
        <span style={{fontSize:12, color:"#b5b5b7"}}>{watchlist.length} item(s)</span>
      </div>
      {watchlist.length === 0 ? (
        <p style={{color:"#b5b5b7", fontSize:13, padding:"6px 2px"}}>Empty list. Click “Save” te kartat e filmave.</p>
      ) : (
        <div style={{display:"grid", gap:8}}>
          {watchlist.map((m) => (
            <div key={m.id} style={S.favItem}>
              <div style={S.favThumb}>
                <img
                  src={ytThumbHQ(m.trailerId) || fallbackImg}
                  onError={(e)=>{e.currentTarget.src=fallbackImg}}
                  alt={`${m.title} thumb`}
                  style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                />
              </div>
              <div>
                <div style={{fontWeight:700, fontSize:14, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{m.title}</div>
                <div style={{fontSize:12, color:"#b5b5b7"}}>{m.year} • {m.length} min</div>
              </div>
              <div style={{display:"flex", gap:6}}>
                <button style={S.btn} onClick={()=>onOpenMovie(m)}><Play size={16}/> Details</button>
                <button style={S.btn} onClick={()=>onRemove(m.id)} title="Remove"><Trash2 size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** ---------- Header ---------- */
function Header({ selectedMood, setSelectedMood, query, setQuery, favoritesUI, isSm }) {
  return (
    <header style={S.header}>
      <div style={{...S.headerInner, gap: isSm ? 8 : 12}}>
        {/* Brand */}
        <div style={S.brand}>
          <div style={{...S.logo, width: isSm?32:36, height: isSm?32:36}}><Play size={18} /></div>
          <div>
            <div style={{...S.title, fontSize: isSm?16:18}}>MoodFlix</div>
            <div className="hide-sm" style={S.sub}>Pick a mood; we pick the movies.</div>
          </div>
        </div>

        {/* Search */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <div style={{ ...S.searchWrap, maxWidth: isSm? "100%" : 420, flex: isSm? "1" : "0 1 420px" }}>
            <Search style={S.searchIcon} size={14} />
            <input
              placeholder="Search titles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{...S.searchInput, padding: isSm?"10px 36px":"10px 38px"}}
            />
            {query && (
              <button style={S.clearBtn} onClick={() => setQuery("")} aria-label="Clear">
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Favorites */}
        <div style={{ marginLeft: "auto" }}>
          {favoritesUI}
        </div>
      </div>

      {/* Mood chips */}
      <nav style={S.nav}>
        <Chip active={!selectedMood} onClick={() => setSelectedMood(null)}>
          <Sun size={16} /> <span className="hide-xs">All</span>
        </Chip>
        {MOODS.map(({ id, label, Icon }) => (
          <Chip key={id} active={selectedMood === id} onClick={() => setSelectedMood(selectedMood === id ? null : id)}>
            <Icon size={16} /> {label}
          </Chip>
        ))}
      </nav>
    </header>
  );
}

/** ---------- Card ---------- */
function MovieCard({ movie, onOpen, inWatchlist, onToggleWatchlist, size="md" }) {
  return (
    <motion.div layout initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}>
      <Card>
        <div style={{ background: moodToGradient[movie.moods[0]] || "#222" }}>
          <Poster id={movie.trailerId} alt={`Poster for ${movie.title}`} big={size!=="md"} />
        </div>
        <div style={S.cardBody}>
          <div style={S.metaRow}>
            <div>
              <div style={S.title3} title={movie.title}>{movie.title}</div>
              <div style={{...S.muted, marginTop: 2}}>{movie.year} • {movie.length} min</div>
            </div>
            <Rating value={movie.rating} />
          </div>

          <p style={{...S.muted, marginTop: 8, lineHeight: 1.5}}>
            {movie.description}
          </p>

          <div style={{...S.metaRow, marginTop: 10}}>
            <div style={S.badgesRow}>
              {movie.moods.slice(0,2).map(m => (
                <span key={m} style={S.badge}>{MOODS.find(x=>x.id===m)?.label||m}</span>
              ))}
            </div>
            <div style={S.actions}>
              <button style={S.btn} onClick={()=>onToggleWatchlist(movie.id)}>
                {inWatchlist ? <><Check size={16}/> In list</> : <><Plus size={16}/> Save</>}
              </button>
              <button style={{...S.btn, ...S.btnPrimary}} onClick={()=>onOpen(movie)}>Details</button>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

/** ---------- Modal ---------- */
function DetailsModal({ movie, onClose, onToggleWatchlist, inWatchlist, isSm }) {
  const modalStyle = isSm
    ? { ...S.modal, maxWidth: "100%", height: "100%", borderRadius: 0, border: "none" }
    : S.modal;
  const topStyle = isSm ? { ...S.modalTop, height: 200 } : S.modalTop;
  return (
    <AnimatePresence>
      {movie && (
        <motion.div style={S.modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
          <motion.div style={modalStyle} initial={{y:40,opacity:0}} animate={{y:0,opacity:1}} exit={{y:40,opacity:0}}>
            <div style={{...topStyle, background: moodToGradient[movie.moods[0]] || "#333" }}>
              <Poster id={movie.trailerId} alt={`Backdrop for ${movie.title}`} big />
              <div style={S.modalTopOverlay}/>
            </div>
            <div style={{...S.modalBody, padding: isSm? 14 : 18}}>
              <div style={S.modalHead}>
                <div>
                  <div style={{...S.title3, fontSize: 20}}>{movie.title}</div>
                  <div style={{...S.muted, marginTop: 4}}>{movie.year} • {movie.length} min</div>
                </div>
                <button style={S.btn} onClick={onClose} aria-label="Close"><X size={20}/></button>
              </div>

              <div style={{...S.metaRow, marginTop: 12}}>
                <Rating value={movie.rating} />
                <div style={{display:"flex", gap: 8, flexWrap: "wrap"}}>
                  {movie.moods.map(m => (
                    <span key={m} style={S.badge}>{MOODS.find(x=>x.id===m)?.label||m}</span>
                  ))}
                </div>
              </div>

              <p style={{marginTop:12, lineHeight:1.6}}>{movie.description}</p>

              <div style={{...S.actions, marginTop: 16, flexWrap: "wrap"}}>
                <a style={{...S.btn, ...S.btnPrimary}} href={`https://www.youtube.com/watch?v=${movie.trailerId}`} target="_blank" rel="noreferrer">
                  <Play size={18}/> Watch trailer
                </a>
                <button style={S.btn} onClick={()=>onToggleWatchlist(movie.id)}>
                  {inWatchlist ? <><Check size={18}/> In list</> : <><Plus size={18}/> Save for later</>}
                </button>
                <a style={S.btn} href={`https://www.google.com/search?q=${encodeURIComponent(movie.title + " streaming")}`} target="_blank" rel="noreferrer">
                  <LinkIcon size={18}/> Where to watch
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** ---------- App ---------- */
export default function MoodFlix(){
  const { w, isSm } = useDevice();
  const [selectedMood, setSelectedMood] = useState(null);
  const [query, setQuery] = useState("");
  const [openMovie, setOpenMovie] = useState(null);
  const [favOpen, setFavOpen] = useState(false);

  const [watchlistIds, setWatchlistIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("moodflix_watchlist")) || []; }
    catch { return []; }
  });
  useEffect(()=>{ localStorage.setItem("moodflix_watchlist", JSON.stringify(watchlistIds)); },[watchlistIds]);
  useEffect(()=>{ document.documentElement.style.background = "#0b0b0c"; },[]);

  // Close favorites when clicking outside (works for both desktop & mobile panel)
  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      if (favOpen && t.closest && !t.closest('[aria-label="Favorites"]') && !t.closest('[role="dialog"]')) {
        setFavOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [favOpen]);

  const toggleWatch = (id) =>
    setWatchlistIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  const has = (id) => watchlistIds.includes(id);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATALOG.filter(m => {
      const moodOk = selectedMood ? m.moods.includes(selectedMood) : true;
      const qOk = q ? m.title.toLowerCase().includes(q) || m.description.toLowerCase().includes(q) : true;
      return moodOk && qOk;
    });
  }, [selectedMood, query]);

  const watchlistFull = useMemo(
    () => watchlistIds.map(id => CATALOG.find(x=>x.id===id)).filter(Boolean),
    [watchlistIds]
  );

  const cols = useCols(w);

  const favoritesUI = (
    <>
      <FavoritesButton
        items={watchlistFull}
        onOpenToggle={()=>setFavOpen(o=>!o)}
      />
      <FavoritesPanel
        open={favOpen}
        watchlist={watchlistFull}
        onOpenMovie={(m)=>{ setOpenMovie(m); setFavOpen(false); }}
        onRemove={(id)=>setWatchlistIds(prev=>prev.filter(x=>x!==id))}
        isSm={isSm}
      />
    </>
  );

  return (
    <div style={S.shell}>
      {/* Responsive utility CSS (for tiny class-based tweaks) */}
      <style>{`
        .hide-sm { display: inline; }
        .hide-xs { display: inline; }
        @media (max-width: 639px){ .hide-sm { display:none!important } }
        @media (max-width: 479px){ .hide-xs { display:none!important } }
        /* nicer horizontal chip scroll */
        nav::-webkit-scrollbar { height: 6px; }
        nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,.12); border-radius: 999px; }
        nav { scrollbar-width: thin; }
      `}</style>

      <Header
        selectedMood={selectedMood}
        setSelectedMood={setSelectedMood}
        query={query}
        setQuery={setQuery}
        favoritesUI={favoritesUI}
        isSm={isSm}
      />

      <main style={{...S.container, padding: isSm? "16px 12px" : "24px 16px"}}>
        {/* Results ONLY */}
        <section style={{margin:"16px 0 8px"}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
            <h2 style={{fontWeight:800, fontSize:18}}>
              {selectedMood ? `Results for: ${MOODS.find(m=>m.id===selectedMood)?.label}` : "All movies"}
            </h2>
            <div style={{...S.muted, fontSize:12}}>{results.length} result(s)</div>
          </div>

          {results.length===0 ? (
            <div style={S.card}>
              <div style={{...S.cardBody, textAlign:"center", padding:"28px 12px"}}>
                <p style={{...S.muted, marginBottom:12}}>No movies found for this filter.</p>
                <button style={{...S.btn, ...S.btnPrimary}} onClick={()=>{ setQuery(""); setSelectedMood(null); }}>
                  Reset
                </button>
              </div>
            </div>
          ) : (
            <motion.div layout style={S.grid(cols)}>
              {results.map(m => (
                <MovieCard key={m.id} movie={m} onOpen={setOpenMovie} inWatchlist={has(m.id)} onToggleWatchlist={toggleWatch}/>
              ))}
            </motion.div>
          )}
        </section>
      </main>

      <DetailsModal
        movie={openMovie}
        onClose={()=>setOpenMovie(null)}
        onToggleWatchlist={toggleWatch}
        inWatchlist={openMovie ? has(openMovie.id) : false}
        isSm={isSm}
      />

      <footer style={S.footer}>
        © {new Date().getFullYear()} MoodFlix • by Dion Mahmuti
      </footer>
    </div>
  );
}
